/**
 * Portal Component
 *
 * The following code is a derivative of the amazing work done by the Material UI team.
 * Original source: https://github.com/mui-org/material-ui/blob/master/packages/material-ui/src/Portal/Portal.js
 */
import React, { Children, cloneElement, forwardRef, useState } from 'react';
import { createPortal, findDOMNode } from 'react-dom';

import { setRef, useEnhancedEffect, useForkRef } from '../utils';

export interface IPortalProps {
  /**
   * The children to render into the `container`.
   */
  children: React.ReactNode & { ref?: React.Ref<HTMLButtonElement> };
  /**
   * A node, component instance, or function that returns either.
   * The `container` will have the portal children appended to it.
   * By default, it uses the body of the top-level document object,
   * so it's simply `document.body` most of the time.
   */
  container?: React.ReactInstance | (() => React.ReactInstance | null) | null;
  /**
   * If `true`, the children stay within it's parent DOM hierarchy.
   */
  isDisabled?: boolean;
  /**
   * Callback fired once the children has been mounted into the `container`.
   */
  onRendered?(): void;
}

function getContainer(container) {
  const containerr = typeof container === 'function' ? container() : container;
  return findDOMNode(containerr);
}

const PortalComp: React.FunctionComponent<IPortalProps> = (
  { children, container, isDisabled = false, onRendered },
  ref
) => {
  const [mountNode, setMountNode] = useState(null);
  const handleRef = useForkRef(children.ref, ref);

  useEnhancedEffect(() => {
    if (!isDisabled) {
      setMountNode(getContainer(container) || document.body);
    }
  }, [container, isDisabled]);

  useEnhancedEffect(() => {
    if (mountNode && !isDisabled) {
      setRef(ref, mountNode);
      return () => {
        setRef(ref, null);
      };
    }

    return undefined;
  }, [ref, mountNode, isDisabled]);

  useEnhancedEffect(() => {
    if (onRendered && (mountNode || isDisabled)) {
      onRendered();
    }
  }, [onRendered, mountNode, isDisabled]);

  if (isDisabled) {
    Children.only(children);
    return cloneElement(children as any, {
      ref: handleRef
    });
  }

  return mountNode ? createPortal(children, mountNode) : mountNode;
};

PortalComp.displayName = 'Portal';

const PortalRefWrap = () => forwardRef(PortalComp);
export const Portal = PortalRefWrap();
